import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Icon, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, useToast, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, Input, Switch, Flex, Divider, Code,
  Stat, StatLabel, StatNumber, StatGroup,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import {
  FiRefreshCw, FiTrash2, FiEdit, FiUserPlus, FiLock,
  FiKey, FiShield, FiUser, FiUsers,
} from 'react-icons/fi';

const API_BASE = '/api/v1/admin';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function SuperAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Create modal
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const [createForm, setCreateForm] = useState({
    username: '', email: '', password: '', first_name: '', last_name: '',
    is_staff: true, is_superuser: false,
  });
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);

  // Edit modal
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Password modal
  const { isOpen: isPwOpen, onOpen: onPwOpen, onClose: onPwClose } = useDisclosure();
  const [pwUser, setPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/users/`, { headers: getAuthHeaders() });
      if (res.status === 403) {
        setError('Access denied. Superuser privileges required.');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Create ────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/users/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(createForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || JSON.stringify(data));
      setNewToken(data.token);
      toast({ title: `User "${data.user.username}" created`, status: 'success', duration: 3000 });
      fetchUsers();
      setCreateForm({ username: '', email: '', password: '', first_name: '', last_name: '', is_staff: true, is_superuser: false });
    } catch (err) {
      toast({ title: 'Failed to create user', description: err.message, status: 'error', duration: 5000 });
    }
    setCreating(false);
  };

  const closeCreate = () => {
    setNewToken(null);
    onCreateClose();
  };

  // ── Edit ──────────────────────────────────────────────────────────────────

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      is_active: user.is_active,
      is_staff: user.is_staff,
      is_superuser: user.is_superuser,
    });
    onEditOpen();
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/users/${editUser.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || JSON.stringify(data));
      toast({ title: `User "${data.user.username}" updated`, status: 'success', duration: 3000 });
      fetchUsers();
      onEditClose();
    } catch (err) {
      toast({ title: 'Failed to update user', description: err.message, status: 'error', duration: 5000 });
    }
    setSaving(false);
  };

  // ── Password ──────────────────────────────────────────────────────────────

  const openPassword = (user) => {
    setPwUser(user);
    setNewPassword('');
    onPwOpen();
  };

  const handlePassword = async () => {
    setPwSaving(true);
    try {
      const res = await fetch(`${API_BASE}/users/${pwUser.id}/password/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || JSON.stringify(data));
      toast({ title: `Password updated for "${pwUser.username}"`, status: 'success', duration: 3000 });
      onPwClose();
    } catch (err) {
      toast({ title: 'Failed to reset password', description: err.message, status: 'error', duration: 5000 });
    }
    setPwSaving(false);
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (user) => {
    if (!window.confirm(`Permanently delete user "${user.username}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/users/${user.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || JSON.stringify(data));
      toast({ title: `User "${user.username}" deleted`, status: 'success', duration: 3000 });
      fetchUsers();
    } catch (err) {
      toast({ title: 'Failed to delete user', description: err.message, status: 'error', duration: 5000 });
    }
  };

  // ── Token ─────────────────────────────────────────────────────────────────

  const handleRegenToken = async (user) => {
    if (!window.confirm(`Regenerate auth token for "${user.username}"? The old token will stop working immediately.`)) return;
    try {
      const res = await fetch(`${API_BASE}/users/${user.id}/token/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || JSON.stringify(data));
      toast({
        title: `New token for "${user.username}"`,
        description: data.token,
        status: 'info',
        duration: 15000,
        isClosable: true,
      });
      fetchUsers();
    } catch (err) {
      toast({ title: 'Failed to regenerate token', description: err.message, status: 'error', duration: 5000 });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  const superuserCount = users.filter(u => u.is_superuser).length;
  const activeCount = users.filter(u => u.is_active).length;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Super Admin</Heading>
          <Text color="gray.500">Manage admin users and access credentials</Text>
        </VStack>
        <HStack spacing={3}>
          <Button leftIcon={<FiRefreshCw />} colorScheme="gray" variant="outline" onClick={fetchUsers}>
            Refresh
          </Button>
          <Button leftIcon={<FiUserPlus />} colorScheme="teal" onClick={onCreateOpen}>
            Create User
          </Button>
        </HStack>
      </HStack>

      {/* Summary stats */}
      <StatGroup mb={6} bg="white" p={6} borderRadius="lg" boxShadow="sm">
        <Stat>
          <StatLabel color="gray.500">Total Users</StatLabel>
          <StatNumber>{users.length}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel color="gray.500">Active</StatLabel>
          <StatNumber color="green.500">{activeCount}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel color="gray.500">Inactive</StatLabel>
          <StatNumber color="red.400">{users.length - activeCount}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel color="gray.500">Superusers</StatLabel>
          <StatNumber color="purple.500">{superuserCount}</StatNumber>
        </Stat>
      </StatGroup>

      {/* Users table */}
      <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>User</Th>
              <Th>Email</Th>
              <Th>Roles</Th>
              <Th>Status</Th>
              <Th>Last Login</Th>
              <Th>Joined</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id} _hover={{ bg: 'gray.50' }}>
                <Td>
                  <HStack spacing={2}>
                    <Icon as={user.is_superuser ? FiShield : FiUser} color={user.is_superuser ? 'purple.500' : 'gray.400'} />
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium" fontSize="sm">{user.username}</Text>
                      {(user.first_name || user.last_name) && (
                        <Text fontSize="xs" color="gray.500">{user.first_name} {user.last_name}</Text>
                      )}
                    </VStack>
                  </HStack>
                </Td>
                <Td>
                  <Text fontSize="sm" color="gray.600">{user.email || '—'}</Text>
                </Td>
                <Td>
                  <HStack spacing={1} flexWrap="wrap">
                    {user.is_superuser && <Badge colorScheme="purple" fontSize="xs">superuser</Badge>}
                    {user.is_staff && <Badge colorScheme="blue" fontSize="xs">staff</Badge>}
                    {user.has_token && <Badge colorScheme="green" fontSize="xs">token</Badge>}
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={user.is_active ? 'green' : 'red'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>
                  <Text fontSize="xs" color="gray.500">{user.last_login_display}</Text>
                </Td>
                <Td>
                  <Text fontSize="xs" color="gray.500">{user.date_joined_display}</Text>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <Tooltip label="Edit User">
                      <IconButton icon={<FiEdit />} size="sm" variant="ghost" colorScheme="blue" onClick={() => openEdit(user)} aria-label="Edit" />
                    </Tooltip>
                    <Tooltip label="Reset Password">
                      <IconButton icon={<FiLock />} size="sm" variant="ghost" colorScheme="orange" onClick={() => openPassword(user)} aria-label="Password" />
                    </Tooltip>
                    <Tooltip label="Regenerate Token">
                      <IconButton icon={<FiKey />} size="sm" variant="ghost" colorScheme="teal" onClick={() => handleRegenToken(user)} aria-label="Token" />
                    </Tooltip>
                    <Tooltip label="Delete User">
                      <IconButton icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red" onClick={() => handleDelete(user)} aria-label="Delete" />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* ── Create User Modal ──────────────────────────────────────────────── */}
      <Modal isOpen={isCreateOpen} onClose={closeCreate} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Admin User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {newToken ? (
              <Box>
                <Alert status="success" borderRadius="md" mb={4}>
                  <AlertIcon />
                  User created successfully. Save the auth token — it won't be shown again.
                </Alert>
                <FormControl>
                  <FormLabel>Auth Token</FormLabel>
                  <Code display="block" p={3} borderRadius="md" fontSize="sm" colorScheme="green" wordBreak="break-all">
                    {newToken}
                  </Code>
                </FormControl>
              </Box>
            ) : (
              <VStack spacing={4}>
                <HStack spacing={4} w="full">
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Username</FormLabel>
                    <Input size="sm" value={createForm.username} onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Email</FormLabel>
                    <Input size="sm" type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
                  </FormControl>
                </HStack>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Password</FormLabel>
                  <Input size="sm" type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" />
                </FormControl>
                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel fontSize="sm">First Name</FormLabel>
                    <Input size="sm" value={createForm.first_name} onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Last Name</FormLabel>
                    <Input size="sm" value={createForm.last_name} onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))} />
                  </FormControl>
                </HStack>
                <Divider />
                <HStack spacing={8} w="full">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel fontSize="sm" mb={0}>Staff</FormLabel>
                    <Switch isChecked={createForm.is_staff} onChange={e => setCreateForm(f => ({ ...f, is_staff: e.target.checked }))} colorScheme="blue" />
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel fontSize="sm" mb={0}>Superuser</FormLabel>
                    <Switch isChecked={createForm.is_superuser} onChange={e => setCreateForm(f => ({ ...f, is_superuser: e.target.checked }))} colorScheme="purple" />
                  </FormControl>
                </HStack>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {newToken ? (
              <Button onClick={closeCreate}>Done</Button>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={closeCreate}>Cancel</Button>
                <Button
                  colorScheme="teal"
                  onClick={handleCreate}
                  isLoading={creating}
                  isDisabled={!createForm.username || !createForm.email || !createForm.password}
                >
                  Create User
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Edit User Modal ────────────────────────────────────────────────── */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User: {editUser?.username}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Email</FormLabel>
                <Input size="sm" type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
              </FormControl>
              <HStack spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm">First Name</FormLabel>
                  <Input size="sm" value={editForm.first_name || ''} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Last Name</FormLabel>
                  <Input size="sm" value={editForm.last_name || ''} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} />
                </FormControl>
              </HStack>
              <Divider />
              <HStack spacing={6} w="full">
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Active</FormLabel>
                  <Switch isChecked={editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} colorScheme="green" />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Staff</FormLabel>
                  <Switch isChecked={editForm.is_staff} onChange={e => setEditForm(f => ({ ...f, is_staff: e.target.checked }))} colorScheme="blue" />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Superuser</FormLabel>
                  <Switch isChecked={editForm.is_superuser} onChange={e => setEditForm(f => ({ ...f, is_superuser: e.target.checked }))} colorScheme="purple" />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleEdit} isLoading={saving}>Save Changes</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Password Reset Modal ───────────────────────────────────────────── */}
      <Modal isOpen={isPwOpen} onClose={onPwClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reset Password: {pwUser?.username}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel fontSize="sm">New Password</FormLabel>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPwClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handlePassword} isLoading={pwSaving} isDisabled={newPassword.length < 8}>
              Reset Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SuperAdmin;
